/* eslint-disable prefer-rest-params */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/naming-convention */
// https://github.com/jurassix/react-validation-mixin/blob/master/src/components/validationMixin.js
import classNames from 'classnames';
import invariant from 'invariant';
import { flatten, get, isEmpty, values } from 'lodash';
import React from 'react';
import { appContext } from '../../components/appContext';
import { ComponentSetStatePromised } from '../../components/utils/ComponentSetStatePromised';
import { DisplayMessage as DisplayError } from '../../components/utils/displayError';
import { DisplayMessage } from '../../components/utils/displayMessage';
import { ITranslateProps } from '../translate';
import { useTranslate } from '../translate/translate';
import joiNLMessages from './joiNLMessages';
import strategy from './joiValidationStrategy';

export interface ILanguageCodeProps {
    languageCode: string;
}

export interface IValidationState {
    errors: any;
}

export interface IValidationComponentState {
    errors: any;
    submitting?: undefined | boolean;
    submitted?: undefined | boolean;
    failed?: undefined | boolean;
    saved?: undefined | boolean;
    message?: undefined | string;
    errorCode?: undefined | string;
}

function isSome(item: any, ...tests: any[]) {
    if (Array.isArray(tests)) {
        return tests.some((test) => item === test);
    }
    return false;
}

function defined(...items: any[]) {
    if (Array.isArray(items)) {
        return items.every((item) => !isSome(item, null, undefined));
    }
    return false;
}
function flattenErrorsObject(obj: any): string | any[] {
    if (typeof obj === 'string') {
        return obj;
    }

    return flatten(
        values(obj).map((value: any) => {
            if (typeof obj !== 'string') {
                return flattenErrorsObject(value);
            }
            return value;
        }),
    );
}

interface IValidatingComponentProps extends ILanguageCodeProps, ITranslateProps {}

export abstract class ValidatingComponent<
    IProps extends IValidatingComponentProps,
    IState extends IValidationComponentState,
> extends ComponentSetStatePromised<IProps, IState> {
    private validator: {
        validate(
            data: Record<string, unknown>,
            joiSchema: Record<string, unknown>,
            options: any,
            moreJoiOptions: Record<string, unknown>,
            callback: any,
        ): void;
    };

    constructor(props: IProps) {
        super(props);
        this.state = {} as any;
        this.validator = strategy();
    }

    public UNSAFEcomponentWillReceiveProps(newProps: ILanguageCodeProps) {
        if (this.props.languageCode !== newProps.languageCode) {
            this.setState({ errors: {} });
        }
    }

    /* Get current validation messages for a specified key or entire form.
     *
     * @param {?String} key to get messages, or entire form if key is undefined.
     * @return {Array}
     */
    public getValidationMessages(key?: string): any {
        if (isEmpty(this.state.errors)) {
            return [];
        }
        if (key === undefined) {
            return flattenErrorsObject(this.state.errors);
        }
        const validationResult = get(this.state.errors, key, []);
        if (Array.isArray(validationResult) && validationResult.length > 1) {
            return [validationResult[0]];
        }
        return validationResult;
    }

    /* Clear all previous validations
     *
     * @return {void}
     */
    public async clearValidations(callback: any) {
        await this.setStatePromised({ errors: {} });
        if (callback) {
            callback();
        }
    }

    /* Check current validity for a specified key or entire form.
     *
     * @param {?String} key to check validity (entire form if undefined).
     * @return {Boolean}.
     */
    public isValid(key?: string): boolean {
        if (!defined(key)) {
            return isEmpty(this.state.errors);
        }
        return isEmpty(get(this.state.errors, key as string));
    }

    /* Private method that handles executing users callback on validation
     *
     * @param {Object} errors object keyed on data field names.
     * @param {Function} error-first callback containing the validation errors if any.
     */
    public _invokeCallback(key: any, callback: any) {
        if (typeof callback !== 'function') {
            return;
        }
        if (this.isValid(key)) {
            callback();
        } else {
            callback(this.state.errors);
        }
    }

    protected abstract getValidatorData(): any;
    protected abstract getValidatorTypes(): any;
    protected extraValidation?(): { [key: string]: string };
    protected onChanged?(value: any, stateKey: string): Promise<void>;

    /* Method to validate single form key or entire form against the component data.
     *
     * @param {String|Function} key to validate, or error-first containing the validation errors if any.
     * @param {?Function} error-first callback containing the validation errors if any.
     */
    public validate(_keyP?: any, _callbackP?: any) {
        const fallback = arguments.length <= 1 && typeof arguments[0] === 'function' ? arguments[0] : undefined;
        const key = arguments.length <= 1 && typeof arguments[0] === 'function' ? undefined : arguments[0];
        const callback = arguments.length <= 2 && typeof arguments[1] === 'function' ? arguments[1] : fallback;

        const data = this.getValidatorData();
        const schema = this.getValidatorTypes();
        const extraValidationResult = this.extraValidation ? this.extraValidation() : undefined;

        invariant(defined(data), 'Data was not provided to the Validator. Implement "getValidatorData" to return data.');
        invariant(defined(schema), 'A schema was not provided to the Validator. Implement "validatorTypes" to return a validation schema.');

        const options = {
            key,
            prevErrors: this.state.errors,
        };

        let lang = {};

        if (this.props.languageCode === 'nl') {
            lang = { language: joiNLMessages };
        }

        this.validator.validate(data, schema, options, lang, async (nextErrors: any) => {
            let errors = nextErrors;

            if (!key && extraValidationResult) {
                errors = { ...errors, ...extraValidationResult };
            }
            await this.setStatePromised({ errors });
            // eslint-disable-next-line no-underscore-dangle
            this._invokeCallback(key, callback);
        });
    }

    protected renderHelpText(message: string) {
        return <em className="state-error">{message}</em>;
    }

    /* Convenience method to validate a key via an event handler. Useful for
     * onBlur, onClick, onChange, etc...
     *
     * @param {?String} State key to validate
     * @return {function} validation event handler
     */
    public handleValidation(key?: string, callback?: any) {
        this.validate(key, callback);
    }

    protected renderMessage(isDisplayErrorSmall?: boolean) {
        if (this.state.failed === true) {
            let message: string;
            switch (this.state.errorCode) {
                case 'EMAIL_NOT_UNIQUE':
                    message = this.props.t('user.errorEmailNotUnique');
                    break;
                case 'EMAIL_ALREADY_IN_COMPANY':
                    message = this.props.t('user.errorEmailAlreadyInCompany');
                    break;
                case 'EMAIL_ALREADY_IN_TENANT':
                    message = this.props.t('user.errorEmailAlreadyInTenant');
                    break;
                case 'EMAIL_NOT_VALID':
                    message = this.props.t('user.errorEmailNotValid');
                    break;
                case 'NO OWNER':
                    message = this.props.t('object.errorNoOwner');
                    break;
                case 'NO CLIENT':
                    message = this.props.t('object.errorNoClient');
                    break;
                case 'CODE IN USE':
                    message = this.props.t('object.errorCodeNotUnique');
                    break;
                case 'NO INSPECTIONSTANDARD':
                    message = this.props.t('object.errorNoInspectionStandard');
                    break;
                case 'STORAGE_LIMIT_REACHED':
                    message = this.props.t('asset.errorStorageLimitReached');
                    break;
                case 'FILE_TOO_BIG':
                    message = this.props.t('asset.errorFileTooBig');
                    break;
                case 'PASSWORD_TOO_SHORT':
                    message = this.props.t('validation.PASSWORD_TOO_SHORT');
                    break;
                case 'PASSWORD_NO_PUNCTUATION':
                    message = this.props.t('validation.PASSWORD_NO_PUNCTUATION');
                    break;
                default:
                    message = `${this.props.t('validation.general')} ${this.state.errorCode ? `Code: ${this.state.errorCode}` : ''}`;
            }
            return <DisplayError message={message} small={isDisplayErrorSmall} />;
        } else if (this.state && this.state.message) {
            return <DisplayMessage message={this.state.message} messageType="general" />;
        } else if (this.state.submitted === true) {
            return <DisplayMessage message={this.props.t('support.submitted')} messageType="saved" />;
        } else if (this.state.saved === true) {
            return <DisplayMessage message={this.props.t('support.submitted')} messageType="saved" />;
        }
        return null;
    }

    protected async handleChange(value: any, stateKey: string) {
        const newState = {} as any;
        newState[stateKey] = value;
        await this.setStatePromised(newState);

        if (this.onChanged) {
            await this.onChanged(value, stateKey);
        }
    }

    protected getClassesWithDefault(field: string, defaultClasses: any) {
        if (!this.isValid(field)) {
            defaultClasses['state-error'] = true;
        }
        return classNames(defaultClasses);
    }
}

export function useOldValidation<TFormState>({
    getValidatorData,
    getValidatorTypes,
    extraValidation,
    onChanged,
    initialState,
}: {
    initialState: TFormState;
    getValidatorData: (innerFormState: TFormState) => any;
    getValidatorTypes: () => any;
    extraValidation?: () => {
        [key: string]: string;
    };
    onChanged?: (value: any, stateKey: string) => Promise<void>;
}): {
    validate: (keyP?: any, callbackP?: any) => void;
    formState: TFormState;
    handleChange: (value: any, stateKey: string) => Promise<void>;
    setFormState: (state: TFormState) => void;
    handleValidation: (key?: string, callback?: any) => void;
    getValidationMessages: (key: string) => any;
} {
    const [formState, setFormState] = React.useState<TFormState>(initialState);

    const [errors, setErrors] = React.useState({});

    const currentAppContext = React.useContext(appContext.context);

    const validator = strategy();

    async function handleChange(value: any, stateKey: string) {
        // Use the functional updater so that multiple handleChange calls fired within the
        // same event (e.g. changing surveryedOn also updates reportDeadlineOnDate) compose on
        // the latest state instead of each spreading the same stale `formState` closure. With a
        // stale base, the second update would silently clobber the first.
        setFormState((prev) => ({ ...prev, ...{ [stateKey]: value } }));

        if (onChanged) {
            await onChanged(value, stateKey);
        }
    }

    function getValidationMessages(key?: string): any {
        if (isEmpty(errors)) {
            return [];
        }
        if (key === undefined) {
            return flattenErrorsObject(errors);
        }
        const validationResult = get(errors, key) || [];
        if (validationResult.length > 1) {
            return [validationResult[0]];
        }
        return validationResult;
    }

    function validate(_keyP?: any, _callbackP?: any) {
        const fallback = arguments.length <= 1 && typeof arguments[0] === 'function' ? arguments[0] : undefined;
        const key = arguments.length <= 1 && typeof arguments[0] === 'function' ? undefined : arguments[0];
        const callback = arguments.length <= 2 && typeof arguments[1] === 'function' ? arguments[1] : fallback;

        const data = getValidatorData(formState);
        const schema = getValidatorTypes();
        const extraValidationResult = extraValidation ? extraValidation() : undefined;

        invariant(defined(data), 'Data was not provided to the Validator. Implement "getValidatorData" to return data.');
        invariant(defined(schema), 'A schema was not provided to the Validator. Implement "validatorTypes" to return a validation schema.');

        const options = {
            key,
            prevErrors: errors,
        };

        let lang = {};

        if (currentAppContext.languageCode === 'nl') {
            lang = { language: joiNLMessages };
        }

        // eslint-disable-next-line @typescript-eslint/require-await
        validator.validate(data, schema, options, lang, async (nextErrors: any) => {
            let newErrors = nextErrors;

            if (!key && extraValidationResult) {
                newErrors = { ...newErrors, ...extraValidationResult };
            }
            setErrors(newErrors);
            if (callback) {
                callback(Object.keys(newErrors).length === 0 ? undefined : newErrors, data);
            }
        });
    }

    function handleValidation(key?: string, callback?: any) {
        validate(key, callback);
    }

    return {
        validate,
        formState,
        handleChange,
        setFormState,
        handleValidation,
        getValidationMessages,
    };
}

export function ValidationRenderMessage(props: {
    message?: undefined | string;
    errorCode?: undefined | string;
    failed?: undefined | boolean;
    submitted?: undefined | boolean;
    saved?: undefined | boolean;
}) {
    const translateF = useTranslate();

    if (props.message) {
        return <DisplayMessage message={props.message} messageType="general" />;
    } else if (props.failed === true) {
        let message: string;
        switch (props.errorCode) {
            case 'NO CLIENT':
                message = translateF('object.errorNoClient');
                break;
            default:
                message = `${translateF('validation.general')} Code: ${props.errorCode}`;
        }

        return <DisplayMessage message={message} messageType="error" />;
    } else if (props.submitted === true) {
        return <DisplayMessage message={translateF('support.submitted')} messageType="saved" />;
    } else if (props.saved === true) {
        return <DisplayMessage message={translateF('support.submitted')} messageType="saved" />;
    }
    return null;
}

export function ValidationHelpText(props: { message: string }) {
    return (
        <div>
            <em className="state-error">{props.message}</em>
        </div>
    );
}
